import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { DocumentPreviewDialog } from './document-preview-dialog'
import {
  MoreVertical,
  Trash2,
  Search,
  FileIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FolderOpen,
  Eye,
  RefreshCw,
} from 'lucide-react'
import {
  type Document,
  type DocumentStatus,
  type DocumentListResponse,
  formatFileSize,
  DOCUMENT_STATUS_CONFIG,
  FILE_TYPE_LABELS,
} from '@/schemas/document'
import { documentService } from '@/services/document'

interface DocumentListProps {
  projectId: string
  kbId: string
  documents: DocumentListResponse | null
  isLoading: boolean
  onRefresh: () => void
  onPageChange: (page: number) => void
  onStatusFilter: (status: DocumentStatus | undefined) => void
  onSearch: (search: string) => void
}

export function DocumentList({
  projectId,
  kbId,
  documents,
  isLoading,
  onRefresh,
  onPageChange,
  onStatusFilter,
  onSearch,
}: DocumentListProps) {
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null)
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null)
  const [reprocessDoc, setReprocessDoc] = useState<Document | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const handleDelete = async () => {
    if (!deleteDoc) return

    setIsDeleting(true)
    try {
      await documentService.deleteDocument(projectId, kbId, deleteDoc.id)
      onRefresh()
    } catch (error) {
      console.error('Error deleting document:', error)
    } finally {
      setIsDeleting(false)
      setDeleteDoc(null)
    }
  }

  const handleReprocess = async () => {
    if (!reprocessDoc) return

    setIsReprocessing(true)
    try {
      await documentService.reprocessDocument(projectId, kbId, reprocessDoc.id)
      onRefresh()
    } catch (error) {
      console.error('Error reprocessing document:', error)
    } finally {
      setIsReprocessing(false)
      setReprocessDoc(null)
    }
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(searchValue)
  }

  const getFileIcon = (fileType: string) => {
    const label = FILE_TYPE_LABELS[fileType as keyof typeof FILE_TYPE_LABELS]
    return label ? (
      <span className="text-[10px] font-bold text-gray-400">{label}</span>
    ) : (
      <FileIcon className="w-4 h-4 text-gray-400" />
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isEmpty = !isLoading && (!documents || documents.documents.length === 0)

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex-1 sm:max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nome..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9 bg-white/5 border-gray-700"
            />
          </div>
        </form>

        {/* Status Filter */}
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v)
            onStatusFilter(v === 'all' ? undefined : (v as DocumentStatus))
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px] bg-white/5 border-gray-700">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="processing">Processando</SelectItem>
            <SelectItem value="ready">Pronto</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Document Table */}
      <Card variant="glass" className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center text-center p-12">
            <div className="w-16 h-16 rounded-full bg-gray-700/50 flex items-center justify-center mb-4">
              <FolderOpen className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Nenhum documento</h3>
            <p className="text-sm text-gray-400">
              {statusFilter !== 'all' || searchValue
                ? 'Nenhum documento encontrado com os filtros aplicados'
                : 'Faca upload de documentos usando a area acima'}
            </p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="grid grid-cols-[1fr,100px,50px] sm:grid-cols-[1fr,80px,80px,100px,50px] lg:grid-cols-[1fr,100px,100px,120px,150px,50px] gap-2 sm:gap-4 px-4 sm:px-6 py-3 bg-white/5 border-b border-gray-700/50 text-xs font-medium text-gray-400 uppercase tracking-wider">
              <div>Nome</div>
              <div className="hidden sm:block">Tipo</div>
              <div className="hidden sm:block">Tamanho</div>
              <div>Status</div>
              <div className="hidden lg:block">Data</div>
              <div></div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-700/50">
              {documents?.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="grid grid-cols-[1fr,100px,50px] sm:grid-cols-[1fr,80px,80px,100px,50px] lg:grid-cols-[1fr,100px,100px,120px,150px,50px] gap-2 sm:gap-4 px-4 sm:px-6 py-4 items-center hover:bg-white/5 transition-colors"
                >
                  {/* Name */}
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-700/50 flex items-center justify-center flex-shrink-0">
                      {getFileIcon(doc.file_type)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{doc.filename}</p>
                      <p className="text-xs text-gray-500">
                        <span className="sm:hidden uppercase">{doc.file_type} - {formatFileSize(doc.file_size)}</span>
                        {doc.chunk_count > 0 && (
                          <span className="hidden sm:inline">{doc.chunk_count} chunks</span>
                        )}
                        <span className="sm:hidden">{doc.chunk_count > 0 && ` - ${doc.chunk_count} chunks`}</span>
                      </p>
                    </div>
                  </div>

                  {/* Type */}
                  <div className="hidden sm:block text-sm text-gray-400 uppercase">{doc.file_type}</div>

                  {/* Size */}
                  <div className="hidden sm:block text-sm text-gray-400">{formatFileSize(doc.file_size)}</div>

                  {/* Status */}
                  <div>
                    <StatusBadge status={doc.status} errorMessage={doc.error_message} />
                  </div>

                  {/* Date */}
                  <div className="hidden lg:block text-sm text-gray-400">{formatDate(doc.created_at)}</div>

                  {/* Actions */}
                  <div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="glass border-gray-700/50">
                        <DropdownMenuItem
                          onClick={() => setPreviewDoc(doc)}
                          className="focus:bg-white/5"
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setReprocessDoc(doc)}
                          disabled={doc.status === 'pending' || doc.status === 'processing'}
                          className="focus:bg-white/5"
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Reprocessar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteDoc(doc)}
                          className="text-red-400 focus:bg-white/5 focus:text-red-400"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Pagination */}
      {documents && documents.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Mostrando {(documents.page - 1) * documents.page_size + 1} a{' '}
            {Math.min(documents.page * documents.page_size, documents.total)} de {documents.total}{' '}
            documentos
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(documents.page - 1)}
              disabled={documents.page <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm text-gray-400">
              Pagina {documents.page} de {documents.total_pages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(documents.page + 1)}
              disabled={documents.page >= documents.total_pages}
            >
              Proxima
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={!!deleteDoc}
        onOpenChange={() => setDeleteDoc(null)}
        title="Excluir documento"
        description={`Tem certeza que deseja excluir "${deleteDoc?.filename}"? Esta acao nao pode ser desfeita.`}
        confirmLabel="Excluir"
        confirmingLabel="Excluindo..."
        confirmVariant="action-red"
        isProcessing={isDeleting}
        onConfirm={handleDelete}
      />

      {/* Document Preview */}
      <DocumentPreviewDialog
        open={!!previewDoc}
        onOpenChange={() => setPreviewDoc(null)}
        projectId={projectId}
        kbId={kbId}
        document={previewDoc}
      />

      {/* Reprocess Confirmation */}
      <ConfirmationDialog
        open={!!reprocessDoc}
        onOpenChange={() => setReprocessDoc(null)}
        title="Reprocessar documento"
        description={`Deseja reprocessar "${reprocessDoc?.filename}"? Os embeddings serao regenerados.`}
        confirmLabel="Reprocessar"
        confirmingLabel="Reprocessando..."
        confirmVariant="action-emerald"
        isProcessing={isReprocessing}
        onConfirm={handleReprocess}
      />
    </div>
  )
}

// Status Badge Component
function StatusBadge({
  status,
  errorMessage,
}: {
  status: DocumentStatus
  errorMessage: string | null
}) {
  const config = DOCUMENT_STATUS_CONFIG[status]

  return (
    <div className="relative group">
      <span
        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${config.color}`}
      >
        {status === 'processing' && (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        )}
        {config.label}
      </span>
      {status === 'error' && errorMessage && (
        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 rounded-lg text-xs text-red-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg">
          {errorMessage}
        </div>
      )}
    </div>
  )
}
